// NomLoop landing page.
//
// ICP: 25-32, first London flat, aspirational cook on a budget.
// Wedge: shared-ingredient planning — anti meal-kit, works with YOUR supermarket.
// Keep claims measurable: no waste-reduction %, and no "compares prices" (we don't compare).
//
// If the user already has a valid JWT they're signed in — skip straight to /app.

import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { C } from "../theme.js";
import { getJwt } from "../../api.js";
import { CookieBanner } from "../components/CookieBanner.jsx";
import { useCookieConsent } from "../hooks/useCookieConsent.js";

// ── Sub-components ────────────────────────────────────────────────────────────

const BRAND_LOGO_SRC = "/icon.png";

function BrandLockup({ compact = false }) {
  const size = compact ? 22 : 28;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      fontFamily: "Lora, Georgia, serif", fontWeight: 700,
      fontSize: compact ? 16 : 20, color: compact ? C.white : C.green,
    }}>
      <img
        src={BRAND_LOGO_SRC}
        alt=""
        width={size}
        height={size}
        style={{ width: size, height: size, borderRadius: 7, objectFit: "cover", display: "block" }}
      />
      NomLoop
    </span>
  );
}

function Nav() {
  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: "rgba(247,244,239,0.92)", backdropFilter: "blur(10px)",
      borderBottom: `1px solid ${C.border}`,
      padding: "14px clamp(16px, 5vw, 24px)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    }}>
      <BrandLockup />
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Link to="/app" className="nav-signin" style={{
          color: C.grey, fontSize: 14, fontWeight: 600, textDecoration: "none",
        }}>Sign in</Link>
        <Link to="/app" style={{
          padding: "9px clamp(14px, 4vw, 20px)", borderRadius: 99, background: C.green,
          color: C.white, fontWeight: 700, fontSize: 14, textDecoration: "none",
          whiteSpace: "nowrap",
        }}>Get started free</Link>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section style={{
      minHeight: "100vh",
      background: `linear-gradient(165deg, ${C.greenDark} 0%, ${C.green} 60%, ${C.greenLight} 100%)`,
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", textAlign: "center",
      padding: "100px 24px 60px",
    }}>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "6px 16px", borderRadius: 99,
        background: "rgba(255,255,255,0.15)",
        color: C.white, fontSize: 13, fontWeight: 700,
        marginBottom: 28, backdropFilter: "blur(8px)",
      }}>
        🇬🇧 Built for UK households · Free to start
      </div>

      <h1 style={{
        fontFamily: "Lora, Georgia, serif", fontWeight: 700,
        fontSize: "clamp(32px, 8vw, 56px)", color: C.white,
        lineHeight: 1.15, maxWidth: 680, marginBottom: 20,
      }}>
        Plan your week.<br />Share ingredients.<br />Shop smarter.
      </h1>

      <p style={{
        color: "rgba(255,255,255,0.8)", fontSize: "clamp(15px, 3vw, 18px)",
        maxWidth: 520, lineHeight: 1.65, marginBottom: 40,
      }}>
        NomLoop picks meals that share ingredients and builds a pack-rounded grocery
        list you can take to Aldi, Tesco, Sainsbury's, Waitrose and more — no
        subscription box, no lock-in.
      </p>

      <Link to="/app" style={{
        padding: "17px 40px", borderRadius: 99,
        background: C.white, color: C.greenDark,
        fontWeight: 800, fontSize: 18, textDecoration: "none",
        boxShadow: C.shLg, display: "inline-block",
        transition: "transform .15s",
      }}
        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
        onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
      >
        Start planning — it's free →
      </Link>

      <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginTop: 14 }}>
        No credit card.
      </p>

      {/* Proof strip */}
      <div style={{
        display: "flex", gap: 24, flexWrap: "wrap", justifyContent: "center",
        marginTop: 56,
      }}>
        {[
          { emoji: "🍽️", stat: "200+",  label: "recipes across 15 styles" },
          { emoji: "🛒", stat: "8",     label: "UK supermarkets" },
        ].map(({ emoji, stat, label }) => (
          <div key={stat} style={{
            background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)",
            borderRadius: 16, padding: "16px 22px", textAlign: "center",
            minWidth: 120,
          }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{emoji}</div>
            <div style={{
              fontFamily: "Lora, Georgia, serif", fontWeight: 700,
              fontSize: 26, color: C.white, lineHeight: 1,
            }}>{stat}</div>
            <div style={{
              fontSize: 12, color: "rgba(255,255,255,0.65)",
              marginTop: 4, fontWeight: 600,
            }}>{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      number: "01",
      emoji:  "🥘",
      title:  "Pick your style & diet",
      body:   "Choose up to 2 cooking styles and tell us your diet. NomLoop looks for meals that share ingredients across both, so one pack can serve more than one meal.",
    },
    {
      number: "02",
      emoji:  "🗓️",
      title:  "We plan your week",
      body:   "Our algorithm favours combinations of meals that reuse ingredients, to limit leftovers. You choose 3–7 dinners.",
    },
    {
      number: "03",
      emoji:  "🛒",
      title:  "Your basket, instantly",
      body:   "Pick your supermarket — Aldi, Lidl, Tesco, Sainsbury's, Waitrose and more. We build a pack-rounded list with an estimated cost before you shop. Pack sizes and prices are estimates; check them in store.",
    },
  ];

  return (
    <section style={{ background: C.white, padding: "80px 24px" }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <p style={{
          textAlign: "center", color: C.green, fontWeight: 700,
          fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase",
          marginBottom: 12,
        }}>How it works</p>
        <h2 style={{
          fontFamily: "Lora, Georgia, serif", fontWeight: 700,
          fontSize: "clamp(24px, 5vw, 36px)", color: C.charcoal,
          textAlign: "center", marginBottom: 56, lineHeight: 1.25,
        }}>
          From style to shopping list in 3 steps
        </h2>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 28,
        }}>
          {steps.map(s => (
            <div key={s.number} style={{
              padding: "28px 24px", borderRadius: 20,
              border: `1.5px solid ${C.border}`,
              background: C.cream,
            }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
              }}>
                <span style={{
                  fontFamily: "Lora, Georgia, serif", fontWeight: 700,
                  fontSize: 13, color: C.greenMid,
                }}>{s.number}</span>
                <span style={{ fontSize: 28 }}>{s.emoji}</span>
              </div>
              <h3 style={{
                fontWeight: 700, fontSize: 17, color: C.charcoal, marginBottom: 10,
              }}>{s.title}</h3>
              <p style={{ fontSize: 14, color: C.grey, lineHeight: 1.65 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyNomLoop() {
  const benefits = [
    {
      emoji: "🚫📦",
      title: "Not a meal-kit box",
      body:  "No subscription. No pre-portioned delivery. NomLoop works with supermarkets you already use — you buy the groceries, we plan the meals.",
    },
    {
      emoji: "🏪",
      title: "Lists for 8 UK supermarkets",
      body:  "Aldi, Lidl, Tesco, ASDA, Sainsbury's, Morrisons, Waitrose and M&S. Switch any time — the list recalculates for your chosen retailer. Pack sizes and prices are estimates, not live retailer data.",
    },
    {
      emoji: "🥜",
      title: "Allergen & diet aware",
      body:  "Set dietary preferences and allergen exclusions. Recipe information can be incomplete; always check ingredients and product labels.",
    },
    {
      emoji: "📉",
      title: "Ingredient-overlap engine",
      body:  "Our algorithm favours meal combinations with high ingredient overlap, so one bunch of coriander or one block of feta can be used across several meals.",
    },
  ];

  return (
    <section style={{ background: C.cream, padding: "80px 24px" }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <p style={{
          textAlign: "center", color: C.green, fontWeight: 700,
          fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase",
          marginBottom: 12,
        }}>Why NomLoop</p>
        <h2 style={{
          fontFamily: "Lora, Georgia, serif", fontWeight: 700,
          fontSize: "clamp(24px, 5vw, 36px)", color: C.charcoal,
          textAlign: "center", marginBottom: 56, lineHeight: 1.25,
        }}>
          A meal planner built around shared ingredients
        </h2>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 24,
        }}>
          {benefits.map(b => (
            <div key={b.title} style={{
              padding: "28px 24px", borderRadius: 20,
              background: C.white, border: `1.5px solid ${C.border}`,
            }}>
              <div style={{ fontSize: 28, marginBottom: 14 }}>{b.emoji}</div>
              <h3 style={{
                fontWeight: 700, fontSize: 16, color: C.charcoal, marginBottom: 10,
              }}>{b.title}</h3>
              <p style={{ fontSize: 14, color: C.grey, lineHeight: 1.65 }}>{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Retailers() {
  const retailers = [
    { name: "Aldi",        emoji: "🔵", color: "#00539F" },
    { name: "Lidl",        emoji: "🔵", color: "#0050AA" },
    { name: "Tesco",       emoji: "🔵", color: "#005EB8" },
    { name: "ASDA",        emoji: "🟢", color: "#78BE20" },
    { name: "Sainsbury's", emoji: "🟠", color: "#FF6600" },
    { name: "Morrisons",   emoji: "🟡", color: "#FDB913" },
    { name: "Waitrose",    emoji: "🟢", color: "#007B5E" },
    { name: "M&S Food",    emoji: "⚫", color: "#000000" },
  ];

  return (
    <section style={{ background: C.white, padding: "60px 24px" }}>
      <div style={{ maxWidth: 880, margin: "0 auto", textAlign: "center" }}>
        <p style={{
          color: C.grey, fontSize: 14, fontWeight: 600,
          marginBottom: 28, textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}>Shopping lists for</p>
        <div style={{
          display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center",
        }}>
          {retailers.map(r => (
            <div key={r.name} style={{
              padding: "10px 20px", borderRadius: 99,
              border: `1.5px solid ${C.border}`,
              background: C.cream, color: C.charcoal,
              fontWeight: 700, fontSize: 14,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: "50%",
                background: r.color, display: "inline-block", flexShrink: 0,
              }} />
              {r.name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section style={{
      background: `linear-gradient(160deg, ${C.greenDark} 0%, ${C.green} 100%)`,
      padding: "80px 24px", textAlign: "center",
    }}>
      <h2 style={{
        fontFamily: "Lora, Georgia, serif", fontWeight: 700,
        fontSize: "clamp(26px, 5vw, 40px)", color: C.white,
        marginBottom: 16, lineHeight: 1.2,
      }}>
        Ready to plan your week?
      </h2>
      <p style={{
        color: "rgba(255,255,255,0.75)", fontSize: 16,
        maxWidth: 440, margin: "0 auto 36px", lineHeight: 1.6,
      }}>
        Plan meals around shared ingredients and build your grocery list.
        Free to start. No card needed.
      </p>
      <Link to="/app" style={{
        padding: "16px 40px", borderRadius: 99,
        background: C.white, color: C.greenDark,
        fontWeight: 800, fontSize: 17, textDecoration: "none",
        display: "inline-block", boxShadow: C.shLg,
      }}>
        Get started free →
      </Link>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{
      background: C.charcoal, color: "rgba(255,255,255,0.5)",
      padding: "32px 24px", textAlign: "center",
      fontSize: 13,
    }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <p style={{ marginBottom: 12 }}>
          <span style={{ marginRight: 20 }}>
            <BrandLockup compact />
          </span>
          <Link to="/privacy" style={{ color: "rgba(255,255,255,0.5)", marginRight: 16, textDecoration: "none" }}>Privacy</Link>
          <Link to="/cookies" style={{ color: "rgba(255,255,255,0.5)", marginRight: 16, textDecoration: "none" }}>Cookies</Link>
          <Link to="/terms"   style={{ color: "rgba(255,255,255,0.5)", textDecoration: "none" }}>Terms</Link>
        </p>
        <p>
          NomLoop does not currently take part in an affiliate programme. If it does,
          it may earn a commission through{" "}
          <a href="https://www.awin.com" style={{ color: "rgba(255,255,255,0.4)" }}>Awin</a>{" "}
          when you click retailer links. Retailer recommendations will not be
          influenced by commission rates.{" "}
          <Link to="/privacy#affiliate" style={{ color: "rgba(255,255,255,0.4)" }}>Learn more</Link>
        </p>
        <p style={{ marginTop: 12 }}>© {new Date().getFullYear()} NomLoop. All rights reserved.</p>
      </div>
    </footer>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const navigate = useNavigate();
  const { consent, accept: acceptCookies, reject: rejectCookies } = useCookieConsent();

  // Signed-in users skip the landing page entirely.
  useEffect(() => {
    if (getJwt()) navigate("/app", { replace: true });
  }, [navigate]);

  return (
    <div style={{
      fontFamily: "Nunito, -apple-system, sans-serif",
      background: C.cream,
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@600;700&family=Nunito:wght@500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        a { cursor: pointer; }
        @media (max-width: 420px) {
          .nav-signin { display: none; }
        }
      `}</style>

      <Nav />
      <Hero />
      <HowItWorks />
      <WhyNomLoop />
      <Retailers />
      <FinalCTA />
      <Footer />

      <CookieBanner consent={consent} onAccept={acceptCookies} onReject={rejectCookies} />
    </div>
  );
}
